import { useState } from 'react';
import { Surface, Badge, Button, Grid, PageHeader, Input, Select, Modal, Checkbox } from '../../ui/components';
import { Plus, Edit } from 'lucide-react';
import FloatingOwenAssistant from '../../components/common/FloatingOwenAssistant';
import FloatingKnowledge from '../../components/common/FloatingKnowledge';

const mockPricingRules = [
  { id: '1', product: 'Conventional', credit_min: 740, credit_max: 850, ltv_min: 0, ltv_max: 80, base_rate: 6.50, adjustment: 0, is_active: true },
  { id: '2', product: 'Conventional', credit_min: 680, credit_max: 739, ltv_min: 0, ltv_max: 80, base_rate: 6.50, adjustment: 0.25, is_active: true },
  { id: '3', product: 'FHA', credit_min: 580, credit_max: 850, ltv_min: 0, ltv_max: 96.5, base_rate: 6.75, adjustment: 0, is_active: true },
  { id: '4', product: 'VA', credit_min: 620, credit_max: 850, ltv_min: 0, ltv_max: 100, base_rate: 6.25, adjustment: 0, is_active: true },
  { id: '5', product: 'Jumbo', credit_min: 700, credit_max: 850, ltv_min: 0, ltv_max: 80, base_rate: 7.00, adjustment: 0, is_active: true },
];

const AdminPricing = () => {
  const [pricingRules] = useState(mockPricingRules);
  const [selectedRule, setSelectedRule] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const handleEdit = (rule) => {
    setSelectedRule(rule);
    setEditOpen(true);
  };

  const products = [...new Set(pricingRules.map(r => r.product))];

  return (
    <div>
      <PageHeader
        title="Pricing Matrix"
        description="Configure rate adjustments and pricing rules"
        actions={<Button variant="primary" icon={Plus}>Add Pricing Rule</Button>}
      />

      <Grid cols={4} gap="var(--lk-space-4)" style={{ marginBottom: 'var(--lk-space-6)' }}>
        <Surface padding="var(--lk-space-5)">
          <div className="lk-overline" style={{ marginBottom: 4 }}>Total Rules</div>
          <div style={{ fontSize: 'var(--lk-text-2xl)', fontWeight: 700 }}>{pricingRules.length}</div>
        </Surface>
        <Surface padding="var(--lk-space-5)">
          <div className="lk-overline" style={{ marginBottom: 4 }}>Active Rules</div>
          <div style={{ fontSize: 'var(--lk-text-2xl)', fontWeight: 700, color: 'var(--lk-success-600)' }}>{pricingRules.filter(r => r.is_active).length}</div>
        </Surface>
        <Surface padding="var(--lk-space-5)">
          <div className="lk-overline" style={{ marginBottom: 4 }}>Products Covered</div>
          <div style={{ fontSize: 'var(--lk-text-2xl)', fontWeight: 700 }}>{products.length}</div>
        </Surface>
        <Surface padding="var(--lk-space-5)">
          <div className="lk-overline" style={{ marginBottom: 4 }}>Avg Base Rate</div>
          <div style={{ fontSize: 'var(--lk-text-2xl)', fontWeight: 700, color: 'var(--lk-primary-600)' }}>
            {(pricingRules.reduce((sum, r) => sum + r.base_rate, 0) / pricingRules.length).toFixed(2)}%
          </div>
        </Surface>
      </Grid>

      <Surface padding="0" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="lk-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Credit Range</th>
                <th>LTV Range</th>
                <th>Base Rate</th>
                <th>Adjustment</th>
                <th>Effective Rate</th>
                <th>Status</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pricingRules.map((rule) => (
                <tr key={rule.id}>
                  <td><Badge variant="primary">{rule.product}</Badge></td>
                  <td style={{ fontWeight: 600 }}>{rule.credit_min} - {rule.credit_max}</td>
                  <td style={{ fontWeight: 600 }}>{rule.ltv_min}% - {rule.ltv_max}%</td>
                  <td style={{ fontWeight: 600 }}>{Number(rule.base_rate).toFixed(3)}%</td>
                  <td>
                    <Badge variant={rule.adjustment > 0 ? 'warning' : rule.adjustment < 0 ? 'success' : 'neutral'}>
                      {rule.adjustment > 0 ? '+' : ''}{rule.adjustment}%
                    </Badge>
                  </td>
                  <td style={{ fontWeight: 700, color: 'var(--lk-primary-600)' }}>{(rule.base_rate + rule.adjustment).toFixed(2)}%</td>
                  <td>
                    <Badge variant={rule.is_active ? 'success' : 'neutral'}>
                      {rule.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <Button size="sm" variant="ghost" icon={Edit} onClick={() => handleEdit(rule)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Surface>

      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit Pricing Rule"
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button variant="primary">Save Changes</Button>
          </>
        }
      >
        {selectedRule && (
          <>
            <Select label="Product" defaultValue={selectedRule.product}>
              <option value="Conventional">Conventional</option>
              <option value="FHA">FHA</option>
              <option value="VA">VA</option>
              <option value="USDA">USDA</option>
              <option value="Jumbo">Jumbo</option>
            </Select>
            <Grid cols={2} gap="var(--lk-space-3)" style={{ marginTop: 'var(--lk-space-3)' }}>
              <Input label="Min Credit Score" type="number" defaultValue={selectedRule.credit_min} />
              <Input label="Max Credit Score" type="number" defaultValue={selectedRule.credit_max} />
            </Grid>
            <Grid cols={2} gap="var(--lk-space-3)" style={{ marginTop: 'var(--lk-space-3)' }}>
              <Input label="Min LTV (%)" type="number" defaultValue={selectedRule.ltv_min} />
              <Input label="Max LTV (%)" type="number" defaultValue={selectedRule.ltv_max} />
            </Grid>
            <Grid cols={2} gap="var(--lk-space-3)" style={{ marginTop: 'var(--lk-space-3)' }}>
              <Input label="Base Rate (%)" type="number" step="0.125" defaultValue={selectedRule.base_rate} />
              <Input label="Adjustment (%)" type="number" step="0.125" defaultValue={selectedRule.adjustment} />
            </Grid>
            <Checkbox label="Active Rule" checked={selectedRule.is_active} onChange={() => {}} style={{ marginTop: 'var(--lk-space-3)' }} />
          </>
        )}
      </Modal>

      <FloatingOwenAssistant context="admin-pricing" />
      <FloatingKnowledge customContent={{
        label: 'Pricing',
        title: 'Pricing management',
        body: 'Configure platform pricing and fee structures.',
        bullets: ['Set fee schedules', 'Manage pricing tiers', 'Configure discount rules'],
        resources: [{ label: 'Pricing guide', href: '#' }],
      }} />
    </div>
  );
};

export default AdminPricing;
